import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { CHARTER_STATUSES } from 'Modules/Charter/Types/CharterScheduleItem';
import { CharterDateFilterType } from 'Modules/Charter/Services/CharterApi';
import { CharterColumnKeys } from './CharterColumnKeys';

export interface ColumnConfig {
	key: string;
	label: string;
	visible: boolean;
	locked?: boolean;
}

interface ColumnVisibility {
	[key: string]: boolean;
}

/** The sort columns, matching `ChartersListOrderBy` on the server name for name. */
export enum CharterOrderByOption {
	REFERENCE = 'REFERENCE',
	NAME = 'NAME',
	CUSTOMER = 'CUSTOMER',
	FERRY = 'FERRY',
	START_DATE = 'START_DATE',
	END_DATE = 'END_DATE',
	PRICE = 'PRICE',
	STATUS = 'STATUS',
	INVOICE_NUMBER = 'INVOICE_NUMBER',
}

/**
 * Which stretch of the operator's charters the table is showing — the charters equivalent of the
 * bookings table's All / Active / Past-Cancelled.
 *
 * Not a filter of its own on the wire. It sets the date range, because a charter's own dates are the
 * only thing that says whether it has happened; the server has no "past charter" concept to send to.
 */
export type CharterPeriod = 'All' | 'Upcoming' | 'Past';

export const CHARTER_PERIODS: CharterPeriod[] = ['All', 'Upcoming', 'Past'];

export interface StaffChartersFilters {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	dateFilterType: CharterDateFilterType;
	statusFilters: string[];
	ferryFilters: string[];
	orderBy: string;
	orderByDescending: boolean;
	pageNo: number;
}

interface StaffChartersState {
	filters: StaffChartersFilters;

	/**
	 * Which of the three periods the toggle is showing.
	 *
	 * Held beside the filters rather than in them because it is never sent: it is a shorthand for a
	 * date range, and the range is what the query carries. Kept so the toggle can stay lit after a
	 * reload, and so picking a custom range by hand does not silently un-light every button.
	 */
	activeTab: CharterPeriod;

	selectedStatuses: string[];
	selectedFerries: string[];

	columnVisibility: ColumnVisibility;
	visibleColumns: ColumnConfig[];

	setFilters: (filters: StaffChartersFilters) => void;
	/** Switch period, which is what moves the date range. */
	setActiveTab: (tab: CharterPeriod) => void;
	updateFilters: (partialFilters: Partial<StaffChartersFilters>) => void;
	resetFilters: (onResultsPage?: boolean) => void;
	setSelectedStatuses: (statuses: string[]) => void;
	setSelectedFerries: (ferryIds: string[]) => void;
	setSearchString: (searchString?: string) => void;
	setVisibleColumns: (columns: ColumnConfig[]) => void;
	toggleColumn: (key: string) => void;
}

/**
 * Reserved and Booked, not Cancelled.
 *
 * A cancelled charter is the ferry doing nothing — it is off the staff schedule entirely — so it is
 * something you go looking for rather than something you are shown. The parking table's default
 * leaves its cancellations out for the same reason. Every state is one tick away.
 */
const defaultSelectedStatuses: string[] = ['Reserved', 'Booked'];

/**
 * Every state, on a customer's page.
 *
 * The opposite call to the staff-wide table above, and deliberately: a cancelled charter is off the
 * staff schedule, so the customer it was for is the only place it can still be found. Staff are rung
 * up about one as often as about a booked charter.
 */
const defaultUserSelectedStatuses: string[] = [...CHARTER_STATUSES];

/**
 * Midnight this morning, which is where "upcoming" starts.
 *
 * Read when the default is built rather than held as a constant, so a tab left open overnight does
 * not keep yesterday's boundary — the store is rehydrated per session and reset on section entry.
 */
const startOfToday = (): Date => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return today;
};

/**
 * The date range a period means.
 *
 * Read against the charter's own block, which is what `dateFilterType: 'charter'` selects and what
 * the server's filter compares — `from` keeps anything that has not ended, `to` keeps anything that
 * started before the boundary.
 *
 *  * **Upcoming** — floor at this morning: everything not yet finished, including a charter out on
 *    the water right now.
 *  * **Past** — ceiling at last night, so today's charters belong to Upcoming alone rather than
 *    appearing under both.
 *  * **All** — no bound.
 *
 * A charter that began last week and is still running answers to both Upcoming and Past. That is the
 * overlap the server's range filter is built on (it matches anything overlapping the range, not
 * anything contained by it), and it is the honest answer for a charter that is genuinely both.
 */
export const periodDateRange = (
	tab: CharterPeriod,
): { startDate?: Date; endDate?: Date } => {
	if (tab === 'Upcoming') {
		return { startDate: startOfToday(), endDate: undefined };
	}

	if (tab === 'Past') {
		const yesterday = startOfToday();
		yesterday.setDate(yesterday.getDate() - 1);
		return { startDate: undefined, endDate: yesterday };
	}

	return { startDate: undefined, endDate: undefined };
};

export const defaultFilters: StaffChartersFilters = {
	statusFilters: defaultSelectedStatuses,
	ferryFilters: [],
	// The date a reader means by "when is this charter" is the block the ferry is held for, not when
	// somebody typed it in — unlike a booking, which is looked up by when it was taken as often as by
	// when it sails.
	dateFilterType: 'charter',
	// Opens on Upcoming, the way the bookings list opens on Active — without it the table opened on
	// every charter ever taken, which for an operator running a weekly run is pages of finished ones
	// before the next one they actually have to do something about.
	//
	// The **Past** and **All** ends of it are one click away on the period toggle; before that toggle
	// existed, reaching a finished charter meant knowing to clear a date filter nothing named.
	...periodDateRange('Upcoming'),
	orderBy: CharterOrderByOption.START_DATE,
	// Soonest first. Descending put the charter furthest into the future at the top, which is the
	// wrong end of a list whose whole point is what the ferry is about to do.
	orderByDescending: false,
	pageNo: 0,
	searchString: '',
};

/**
 * The same filters, with every state showing and no date boundary — what a customer's charters tab
 * opens on.
 *
 * The opposite call to the staff-wide table for the same reason its status list is: this tab is
 * where somebody looks up a charter they are being rung about, and one that has already happened is
 * as likely to be the one asked about as one that has not.
 */
export const defaultUserCharterFilters: StaffChartersFilters = {
	...defaultFilters,
	statusFilters: defaultUserSelectedStatuses,
	...periodDateRange('All'),
};

/**
 * Every column, and which of them start ticked.
 *
 * The charter's reference is locked and leads: it is the one thing on a row that is always there and
 * always unique, so it is what tells two charters apart. The name used to hold that place and could
 * not do it — it is optional, so an unnamed charter's row read "Charter", and it is not unique, so
 * three charters called "Waste run" were indistinguishable.
 */
const getDefaultColumns = (): ColumnConfig[] => [
	{ key: CharterColumnKeys.REFERENCE, label: 'Charter ID', visible: true, locked: true },
	{ key: CharterColumnKeys.NAME, label: 'Charter', visible: true },
	{ key: CharterColumnKeys.CUSTOMER, label: 'Customer', visible: true },
	{ key: CharterColumnKeys.FERRY, label: 'Ferry', visible: true },
	{ key: CharterColumnKeys.START_DATE, label: 'Ferry held from', visible: true },
	{ key: CharterColumnKeys.END_DATE, label: 'Ferry held until', visible: true },
	{ key: CharterColumnKeys.PRICE, label: 'Price', visible: true },
	{ key: CharterColumnKeys.STATUS, label: 'Status', visible: true },
	{ key: CharterColumnKeys.PAYMENT, label: 'Payment', visible: true },
	{ key: CharterColumnKeys.INVOICE_NUMBER, label: 'Invoice number', visible: true },
];

const buildColumns = (visibility: ColumnVisibility): ColumnConfig[] =>
	getDefaultColumns().map(col => ({
		...col,
		visible: visibility[col.key] ?? col.visible,
	}));

/**
 * Dates come back out of session storage as strings. Revived here rather than at the point of use,
 * because the filter bar's picker and the query's wire format both take a `Date` — a string reaching
 * either reads as an invalid date and silently filters nothing.
 */
const customStorage = {
	getItem: (name: string) => {
		const str = sessionStorage.getItem(name);
		if (!str) return null;

		const parsed = JSON.parse(str);
		if (parsed.state?.filters?.startDate) {
			parsed.state.filters.startDate = new Date(parsed.state.filters.startDate);
		}
		if (parsed.state?.filters?.endDate) {
			parsed.state.filters.endDate = new Date(parsed.state.filters.endDate);
		}

		return JSON.stringify(parsed);
	},
	setItem: (name: string, value: string) => {
		sessionStorage.setItem(name, value);
	},
	removeItem: (name: string) => sessionStorage.removeItem(name),
};

/**
 * The charters table's filters, columns and sort — one store for both the copy on /staff-bookings
 * and the copy on a customer's page, the way the bookings and parking tables share theirs.
 *
 * Persisted to session storage like the tables beside it, so drilling into a charter and coming back
 * lands on the same page of the same filter — see `useResetFiltersOnSectionEntry`, which is what
 * decides whether that persistence is honoured or thrown away on arrival. The copy on a customer's
 * page always resets, so it never opens on filters set on the staff-wide table.
 *
 * No selection state and no export: nothing on this table is done to a set of charters, so the rows
 * carry no checkbox.
 */
export const useStaffChartersStore = create<StaffChartersState>()(
	persist(
		(set, get) => ({
			filters: defaultFilters,
			activeTab: 'Upcoming',
			selectedStatuses: defaultSelectedStatuses,
			selectedFerries: [],
			columnVisibility: {},
			visibleColumns: getDefaultColumns(),

			setFilters: (filters: StaffChartersFilters) => {
				set({
					filters,
					selectedStatuses: filters.statusFilters,
					selectedFerries: filters.ferryFilters,
				});
			},

			// Back to page one with it: the charter on page 3 of Upcoming is not on page 3 of Past, and
			// holding the number is how a table answers a period change with "no charters found".
			setActiveTab: (tab: CharterPeriod) => {
				set({
					activeTab: tab,
					filters: { ...get().filters, ...periodDateRange(tab), pageNo: 0 },
				});
			},

			updateFilters: (partialFilters: Partial<StaffChartersFilters>) => {
				const newFilters = { ...get().filters, ...partialFilters };

				set({
					filters: newFilters,
					selectedStatuses: newFilters.statusFilters,
					selectedFerries: newFilters.ferryFilters,
				});
			},

			// `onResultsPage` is the copy embedded on a customer's page, which starts from its own
			// defaults — see defaultUserCharterFilters.
			resetFilters: (onResultsPage = false) => {
				const next = onResultsPage ? defaultUserCharterFilters : defaultFilters;

				set({
					filters: next,
					// The two defaults differ in period as well as in status — see
					// defaultUserCharterFilters.
					activeTab: onResultsPage ? 'All' : 'Upcoming',
					selectedStatuses: next.statusFilters,
					selectedFerries: next.ferryFilters,
				});
			},

			// Every filter change resets to page one. Staying on page 4 of a narrower result is how a
			// table answers a search with "no charters found" while there are plenty on page 1.
			setSelectedStatuses: (statuses: string[]) => {
				set({
					selectedStatuses: statuses,
					filters: { ...get().filters, statusFilters: statuses, pageNo: 0 },
				});
			},

			setSelectedFerries: (ferryIds: string[]) => {
				set({
					selectedFerries: ferryIds,
					filters: { ...get().filters, ferryFilters: ferryIds, pageNo: 0 },
				});
			},

			setSearchString: (searchString?: string) => {
				set({ filters: { ...get().filters, searchString, pageNo: 0 } });
			},

			setVisibleColumns: (columns: ColumnConfig[]) => {
				const visibility: ColumnVisibility = {};
				columns.forEach(col => {
					visibility[col.key] = col.visible;
				});
				set({ visibleColumns: columns, columnVisibility: visibility });
			},

			toggleColumn: (key: string) => {
				const updatedColumns = get().visibleColumns.map(col =>
					col.key === key && !col.locked ? { ...col, visible: !col.visible } : col,
				);

				const visibility: ColumnVisibility = {};
				updatedColumns.forEach(col => {
					visibility[col.key] = col.visible;
				});

				set({ visibleColumns: updatedColumns, columnVisibility: visibility });
			},
		}),
		{
			name: 'staff-all-charters-filters',
			storage: createJSONStorage(() => customStorage),
			partialize: state => ({
				filters: state.filters,
				activeTab: state.activeTab,
				selectedStatuses: state.selectedStatuses,
				selectedFerries: state.selectedFerries,
				columnVisibility: state.columnVisibility,
			}),
			onRehydrateStorage: () => state => {
				if (!state) return;

				if (state.filters) {
					// A status the enum no longer has would be sent to the server and match nothing, so
					// a stale persisted filter is dropped back to the default rather than carried.
					const statuses = (state.filters.statusFilters ?? []).filter(x =>
						(CHARTER_STATUSES as readonly string[]).includes(x),
					);

					state.filters.statusFilters = statuses.length > 0 ? statuses : defaultSelectedStatuses;
					state.selectedStatuses = state.filters.statusFilters;
					state.selectedFerries = state.filters.ferryFilters ?? [];
				}

				// A period is a date range worked out from "today", and a session can outlive the day it
				// was stored on — so the range is recomputed on the way back in rather than trusted.
				// Without this, a tab left open overnight stays floored at yesterday morning and
				// quietly shows a finished charter under Upcoming.
				if (state.activeTab && state.activeTab !== 'All') {
					state.filters = { ...state.filters, ...periodDateRange(state.activeTab) };
				}

				// Rebuilt from the current definitions so a column added since the visibility was saved
				// appears, with its own default, rather than being missing from the selector.
				state.visibleColumns = buildColumns(state.columnVisibility || {});
			},
		},
	),
);
